import { UIEvent, useEffect, useId, useMemo, useRef, useState } from "react";

import { ArrowDownIcon, CloseIcon, SpinnerIcon } from "@/assets/icons";
import useOnClickOutside from "@/hooks/ui/useClickOutside";

import { twMerge } from "tailwind-merge";

import { IOptionItem } from "./dropdown/types";

export type DropdownMultiSelectApplyProps = {
  data: IOptionItem[];
  /** Committed selection. Options are matched by `value`. */
  value: IOptionItem[];
  /** Called only on Apply and on dismissing a chip, never on ticking an option. */
  onSelect: (items: IOptionItem[]) => void;
  placeholder?: string;
  labelBorder?: string;
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  required?: boolean;
  isValidationError?: boolean;
};

const sameSelection = (a: IOptionItem[], b: IOptionItem[]) => {
  if (a.length !== b.length) return false;
  const bValues = new Set(b.map((item) => item.value));
  return a.every((item) => bValues.has(item.value));
};

const matchesSearch = (item: IOptionItem, term: string) => {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  return (
    item.label.toLowerCase().includes(needle) ||
    (item.description ?? "").toLowerCase().includes(needle)
  );
};

const OptionCheckbox = ({ checked }: { checked: boolean }) => (
  <span
    aria-hidden
    className={twMerge(
      "mt-[2px] flex h-5 w-5 shrink-0 items-center justify-center rounded-md border",
      checked ? "border-teal-500 bg-teal-500" : "border-dim-gray bg-white"
    )}
  >
    {checked && (
      <svg
        className="h-3.5 w-3.5 text-white"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
          clipRule="evenodd"
        />
      </svg>
    )}
  </span>
);

const DropdownMultiSelectApply = ({
  data,
  value,
  onSelect,
  placeholder = "Select",
  labelBorder = placeholder,
  className,
  disabled = false,
  isLoading = false,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  required = false,
  isValidationError = false
}: DropdownMultiSelectApplyProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [draftValues, setDraftValues] = useState<string[]>([]);

  const closeMenu = () => {
    setIsOpen(false);
    setSearchTerm("");
  };

  // Opening always restarts the draft from the committed value, so a draft that
  // was never applied is discarded.
  const openMenu = () => {
    if (isOpen || disabled) return;
    setDraftValues(value.map((item) => item.value));
    setSearchTerm("");
    setIsOpen(true);
  };

  useOnClickOutside(containerRef, closeMenu, [], isOpen);

  // Esc closes from anywhere, including from inside the search field.
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") closeMenu();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  const filteredData = useMemo(
    () => data.filter((item) => matchesSearch(item, searchTerm)),
    [data, searchTerm]
  );

  const draftItems = useMemo(
    () => data.filter((item) => draftValues.includes(item.value)),
    [data, draftValues]
  );

  const isDirty = !sameSelection(draftItems, value);

  const toggleDraft = (optionValue: string) =>
    setDraftValues((current) =>
      current.includes(optionValue)
        ? current.filter((item) => item !== optionValue)
        : [...current, optionValue]
    );

  const handleApply = () => {
    onSelect(draftItems);
    closeMenu();
  };

  const handleRemoveChip = (optionValue: string) =>
    onSelect(value.filter((item) => item.value !== optionValue));

  const handleListScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!hasMore || isLoadingMore || !onLoadMore) return;
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 24) onLoadMore();
  };

  const hasValue = value.length > 0;

  return (
    <div ref={containerRef} className={twMerge("relative", className)}>
      {hasValue && !isOpen && (
        <label
          className={twMerge(
            "pointer-events-none absolute left-2 top-[-10px] z-10 mb-0 max-w-[90%] truncate",
            "bg-white px-2 text-[10px] text-txtBody"
          )}
        >
          {labelBorder}
          {required && <span className="text-cinnabar"> *</span>}
        </label>
      )}

      <div
        className={twMerge(
          "relative flex min-h-[44px] w-full items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm",
          isOpen ? "border-teal-500" : "border-gainsboro",
          isValidationError && "border-cinnabar",
          disabled && "bg-white-smoke"
        )}
      >
        {/* Covers the whole field so a click anywhere toggles the menu. It
            cannot wrap the chips and search field, so those sit on top of it. */}
        <button
          type="button"
          aria-label={labelBorder}
          aria-expanded={isOpen}
          aria-controls={menuId}
          disabled={disabled}
          onClick={() => (isOpen ? closeMenu() : openMenu())}
          className="absolute inset-0 cursor-pointer rounded-lg border-0 bg-transparent p-0 disabled:cursor-not-allowed"
        />

        <div className="flex flex-1 flex-wrap items-center gap-2">
          {isOpen ? (
            <input
              type="search"
              autoFocus
              value={searchTerm}
              placeholder={placeholder}
              onChange={(event) => setSearchTerm(event.target.value)}
              // Would otherwise submit the surrounding form.
              onKeyDown={(event) => {
                if (event.key === "Enter") event.preventDefault();
              }}
              className="relative z-10 w-full border-0 p-0 text-sm outline-none placeholder:text-txtPlaceholder"
            />
          ) : (
            <>
              {!hasValue && (
                <span className="text-txtPlaceholder">{placeholder}</span>
              )}
              {value.map((item) => (
                <span
                  key={item.value}
                  className="relative z-10 flex items-center gap-1 rounded-md border border-teal-300 bg-teal-50 px-2 py-[2px] text-xs text-teal-700"
                >
                  {item.label}
                  {!disabled && (
                    <button
                      type="button"
                      aria-label={`Remove ${item.label}`}
                      // Outside the menu: dismissing commits straight away.
                      onClick={() => handleRemoveChip(item.value)}
                      className="border-0 bg-transparent p-0 leading-none"
                    >
                      <CloseIcon className="h-3 w-3 fill-teal-700" />
                    </button>
                  )}
                </span>
              ))}
            </>
          )}
        </div>

        {isLoading ? (
          <SpinnerIcon className="h-4 w-4 shrink-0 animate-spin" />
        ) : (
          <ArrowDownIcon
            className={twMerge("h-4 w-4 shrink-0", isOpen && "rotate-180")}
          />
        )}
      </div>

      {isOpen && (
        // Only the option list scrolls: a footer inside the scroll port lets
        // options show through the menu's padding underneath it.
        <div
          id={menuId}
          className="absolute z-20 mt-1 flex max-h-[300px] w-full flex-col rounded-lg border border-gainsboro bg-white p-3 shadow-md"
        >
          <div
            onScroll={handleListScroll}
            className="min-h-0 flex-1 overflow-y-auto"
          >
            <fieldset>
              <legend className="sr-only">{labelBorder}</legend>
              {filteredData.length === 0 && (
                <p className="py-2 text-center text-sm text-txtBody">
                  No data found
                </p>
              )}
              {filteredData.map((item) => {
                const checked = draftValues.includes(item.value);
                return (
                  <label
                    key={item.value}
                    className="relative mb-0 flex cursor-pointer items-start gap-2 rounded-md px-1 py-2 focus-within:bg-white-smoke hover:bg-white-smoke"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleDraft(item.value)}
                      className="sr-only"
                    />
                    <OptionCheckbox checked={checked} />
                    <span className="flex flex-col">
                      <span className="text-sm font-semibold text-txtHeading">
                        {item.label}
                      </span>
                      {item.description && (
                        <span className="text-xs text-txtBody">
                          {item.description}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
              {isLoadingMore && (
                <p className="py-2 text-center text-xs text-txtBody">
                  Loading…
                </p>
              )}
            </fieldset>
          </div>

          <div className="flex shrink-0 gap-2 pt-3">
            <button
              type="button"
              onClick={() => setDraftValues([])}
              className="flex-1 rounded-lg border border-gainsboro bg-white py-2 text-sm font-semibold text-txtHeading"
            >
              Reset
            </button>
            <button
              type="button"
              disabled={!isDirty}
              onClick={handleApply}
              className={twMerge(
                "flex-1 rounded-lg border py-2 text-sm font-semibold",
                isDirty
                  ? "border-teal-500 bg-white text-teal-500"
                  : "border-gainsboro bg-white text-light-gray"
              )}
            >
              {draftValues.length > 0
                ? `Apply (${draftValues.length})`
                : "Apply"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DropdownMultiSelectApply;
